import { useEffect, useState, useRef } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  query,
} from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Real-time subscription to a Firestore collection with the given constraints.
 */
export const useFirestoreCollection = (collectionName, constraints = []) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const unsubscribeRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, ...constraints);

    unsubscribeRef.current = onSnapshot(
      q,
      (snapshot) => {
        setData(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error(`Error subscribing to ${collectionName}:`, err); // nosemgrep: javascript.lang.security.audit.unsafe-formatstring.unsafe-formatstring - console log message only
        setError(err);
        setLoading(false);
      }
    );

    return () => {
      if (unsubscribeRef.current) unsubscribeRef.current();
    };
    // Constraints are objects from firestore SDK; comparing by JSON is good enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionName, JSON.stringify(constraints)]);

  return { data, loading, error };
};

/** Real-time subscription to a single Firestore document. */
export const useFirestoreDocument = (collectionName, docId) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const unsubscribeRef = useRef(null);

  useEffect(() => {
    if (!docId) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setError(null);

    const docRef = doc(db, collectionName, docId);
    unsubscribeRef.current = onSnapshot(
      docRef,
      (snapshot) => {
        setData(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null);
        setLoading(false);
      },
      (err) => {
        console.error('Error subscribing to document:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => {
      if (unsubscribeRef.current) unsubscribeRef.current();
    };
  }, [collectionName, docId]);

  return { data, loading, error };
};

/** Combined counts across standalone and business-embedded vets. */
const vetCounts = (standalone, embedded) => {
  const all = [...standalone, ...embedded];
  return {
    totalVets: all.length,
    pendingVets: all.filter((v) => v.verificationStatus === 'pending').length,
    verifiedVets: all.filter(
      (v) => v.verificationStatus === 'approved' || v.verificationStatus === 'verified'
    ).length,
    rejectedVets: all.filter((v) => v.verificationStatus === 'rejected').length,
  };
};

/**
 * Live dashboard stats subscribed across users / businesses / vets / orders / reports.
 * Uses verificationStatus (not status) for businesses + vets, matching the schema set
 * by Cloud Functions (adminApproveBusiness, adminApproveVet, etc).
 */
export const useDashboardStats = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    newUsersToday: 0,
    activeUsers: 0,
    bannedUsers: 0,
    totalBusinesses: 0,
    pendingBusinesses: 0,
    verifiedBusinesses: 0,
    rejectedBusinesses: 0,
    totalVets: 0,
    pendingVets: 0,
    verifiedVets: 0,
    rejectedVets: 0,
    totalOrders: 0,
    ordersThisWeek: 0,
    pendingReports: 0,
    resolvedReports: 0,
    totalReports: 0,
    totalWelfareReports: 0,
    openWelfareReports: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // The two vet sources arrive on independent snapshots; refs let either one
  // recompute the combined totals without waiting for the other.
  const standaloneVetsRef = useRef([]);
  const embeddedVetsRef = useRef([]);

  useEffect(() => {
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const toDate = (ts) => {
      if (!ts) return null;
      if (typeof ts.toDate === 'function') return ts.toDate();
      const d = new Date(ts);
      return isNaN(d.getTime()) ? null : d;
    };
    const tsAfter = (ts, threshold) => {
      const d = toDate(ts);
      return d && d >= threshold;
    };

    const subscribe = (col, handler) =>
      onSnapshot(
        collection(db, col),
        (snap) => handler(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        (err) => {
          console.error(`Stats subscription failed for ${col}:`, err); // nosemgrep: javascript.lang.security.audit.unsafe-formatstring.unsafe-formatstring - console log message only
          setError(err);
        }
      );

    const unsubs = [
      subscribe('users', (users) => {
        setStats((prev) => ({
          ...prev,
          totalUsers: users.length,
          newUsersToday: users.filter((u) => tsAfter(u.createdAt, startOfDay)).length,
          activeUsers: users.filter((u) =>
            tsAfter(u.lastSeen, sevenDaysAgo) ||
            tsAfter(u.lastActiveAt, sevenDaysAgo) ||
            tsAfter(u.createdAt, sevenDaysAgo)
          ).length,
          bannedUsers: users.filter((u) => u.isBanned === true).length,
        }));
      }),
      // Businesses double as a vet source: vets added through
      // AddVetToBusinessScreen live in businesses.vetServices.veterinarians[],
      // NOT in the `veterinarians` collection (which is empty in production).
      // Counting only the collection reported 0 vets on the dashboard while
      // real vets sat pending — the same bug the Veterinarians tab had.
      subscribe('businesses', (businesses) => {
        embeddedVetsRef.current = businesses.flatMap((b) => {
          const list = b.vetServices && Array.isArray(b.vetServices.veterinarians)
            ? b.vetServices.veterinarians
            : [];
          return list.map((v) => ({
            ...v,
            verificationStatus: v.verificationStatus || 'pending',
          }));
        });
        setStats((prev) => ({
          ...prev,
          totalBusinesses: businesses.length,
          pendingBusinesses: businesses.filter((b) => b.verificationStatus === 'pending').length,
          verifiedBusinesses: businesses.filter((b) => b.verificationStatus === 'verified').length,
          rejectedBusinesses: businesses.filter((b) => b.verificationStatus === 'rejected').length,
          ...vetCounts(standaloneVetsRef.current, embeddedVetsRef.current),
        }));
      }),
      subscribe('veterinarians', (vets) => {
        standaloneVetsRef.current = vets;
        setStats((prev) => ({
          ...prev,
          ...vetCounts(standaloneVetsRef.current, embeddedVetsRef.current),
        }));
      }),
      subscribe('orders', (orders) => {
        setStats((prev) => ({
          ...prev,
          totalOrders: orders.length,
          ordersThisWeek: orders.filter((o) => tsAfter(o.createdAt, sevenDaysAgo)).length,
        }));
      }),
      // The moderation queue is `content_reports`. The `reports` collection holds
      // animal-welfare case files whose status vocabulary is 'Submitted' /
      // 'Closed', so counting 'pending' there always returned 0 and the
      // dashboard claimed there was nothing to moderate.
      subscribe('content_reports', (reports) => {
        setStats((prev) => ({
          ...prev,
          totalReports: reports.length,
          pendingReports: reports.filter((r) => r.status === 'pending').length,
          resolvedReports: reports.filter((r) => r.status === 'resolved').length,
        }));
      }),
      subscribe('reports', (welfare) => {
        setStats((prev) => ({
          ...prev,
          totalWelfareReports: welfare.length,
          openWelfareReports: welfare.filter(
            (r) => r.status !== 'Closed' && r.status !== 'closed'
          ).length,
        }));
      }),
    ];

    setLoading(false);
    return () => unsubs.forEach((u) => u && u());
  }, []);

  return { stats, loading, error };
};

const coerceDate = (ts) => {
  if (!ts) return null;
  if (typeof ts.toDate === 'function') return ts.toDate();
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
};

const buildSeries = (timestamps, days) => {
  const buckets = new Map();
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - i);
    buckets.set(d.toISOString().split('T')[0], 0);
  }
  for (const ts of timestamps) {
    const d = coerceDate(ts);
    if (!d) continue;
    d.setHours(0, 0, 0, 0);
    const key = d.toISOString().split('T')[0];
    if (buckets.has(key)) buckets.set(key, buckets.get(key) + 1);
  }
  return Array.from(buckets.entries()).map(([date, count]) => ({ date, count }));
};

/** Live user-creation series for the last `days` days. */
export const useUserGrowthData = (days = 14) => {
  const [growthData, setGrowthData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'users'), (snap) => {
      const timestamps = snap.docs.map((d) => d.data().createdAt);
      setGrowthData(buildSeries(timestamps, days));
      setLoading(false);
    });
    return () => unsubscribe();
  }, [days]);

  return { growthData, loading };
};

/** Live order-creation series for the last `days` days. */
export const useOrdersTrend = (days = 14) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'orders'), (snap) => {
      const timestamps = snap.docs.map((d) => d.data().createdAt);
      setData(buildSeries(timestamps, days));
      setLoading(false);
    });
    return () => unsubscribe();
  }, [days]);

  return { data, loading };
};

/** Generic live admin collection — same contract as useFirestoreCollection. */
export const useAdminCollection = useFirestoreCollection;

const hooks = {
  useFirestoreCollection,
  useFirestoreDocument,
  useDashboardStats,
  useUserGrowthData,
  useOrdersTrend,
  useAdminCollection,
};

export default hooks;
